import React, { useState } from 'react';
import { XMarkIcon, HeartIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';

const QuickViewModal = ({
  product,
  isOpen,
  onClose,
  onAddToCart,
  onToggleWishlist,
  isWishlisted,
  formatPrice,
  renderStars,
  user
}) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  if (!isOpen || !product) return null;

  const getCurrentPrice = () => {
    if (user?.role === 'company_admin' || user?.role === 'company_user') {
      return product.price_b2b || product.price_b2c;
    }
    return product.price_b2c;
  };

  const handleAddToCart = () => {
    onAddToCart(product, quantity);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 left-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {/* Product Images */}
            <div className="relative">
              <div className="aspect-square bg-gray-100">
                <img
                  src={
                    product.images?.[selectedImage] 
                      ? (product.images[selectedImage].startsWith('http') 
                          ? product.images[selectedImage] 
                          : `/api${product.images[selectedImage]}`)
                      : 'https://picsum.photos/600/600'
                  }
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Image Thumbnails */}
              {product.images && product.images.length > 1 && (
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex gap-2 overflow-x-auto">
                    {product.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                          selectedImage === index 
                            ? 'border-primary-500' 
                            : 'border-white hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={image.startsWith('http') ? image : `/api${image}`}
                          alt={`${product.name} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="p-6 flex flex-col">
              {/* Brand */}
              {product.brand && (
                <p className="text-sm text-gray-500 mb-2">{product.brand}</p>
              )}

              {/* Product Name */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {product.name}
              </h2>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-4">
                {renderStars(product.rating_average || 0)}
                <span className="text-sm text-gray-500">
                  ({product.rating_count || 0} ביקורות)
                </span>
              </div>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-center gap-4 mb-2">
                  <span className="text-3xl font-bold text-primary-600">
                    {formatPrice(getCurrentPrice())}
                  </span>
                  
                  {/* Stock Status */}
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    product.stock_quantity > 0 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {product.stock_quantity > 0 
                      ? `${product.stock_quantity} במלאי` 
                      : 'אזל מהמלאי'
                    }
                  </div>
                </div>
                
                {user?.role === 'company_admin' || user?.role === 'company_user' ? (
                  <div className="text-sm text-gray-500">
                    מחיר B2B: {formatPrice(product.price_b2b)}
                  </div>
                ) : null}
              </div>

              {/* Description */}
              {product.short_description && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">תיאור</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {product.short_description}
                  </p>
                </div>
              )}

              {/* Quantity Selector */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-2">כמות</h3>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
                  >
                    -
                  </button>
                  <span className="w-16 text-center font-medium">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(product.stock_quantity, quantity + 1))}
                    disabled={quantity >= product.stock_quantity}
                    className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-auto space-y-3">
                <button
                  onClick={handleAddToCart}
                  disabled={product.stock_quantity === 0}
                  className={`w-full py-3 px-6 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                    product.stock_quantity === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-lg'
                  }`}
                >
                  <ShoppingCartIcon className="h-5 w-5" />
                  {product.stock_quantity === 0 ? 'אזל מהמלאי' : 'הוסף לעגלה'}
                </button>

                <button
                  onClick={() => onToggleWishlist(product.id)}
                  className={`w-full py-3 px-6 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 border ${
                    isWishlisted
                      ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {isWishlisted ? (
                    <HeartIconSolid className="h-5 w-5" />
                  ) : (
                    <HeartIcon className="h-5 w-5" />
                  )}
                  {isWishlisted ? 'הסר מהמשאלות' : 'הוסף למשאלות'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickViewModal;
